import React, { useState } from 'react'
import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material'

import GoogleIcon from '../../../../components/icon/GoogleIcon'
import { REFUND_APPROVAL_TEXT } from '../../../constants/refundApprovalText'
import {
  UI_COLOR_BORDER_MEDIUM,
  UI_COLOR_BORDER_NEUTRAL,
  UI_COLOR_ICON_GRAY,
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_ALT,
  UI_COLOR_PRIMARY_SOFT_FAINT,
  UI_COLOR_SURFACE_GHOST,
  UI_COLOR_TEXT_DISABLED,
  UI_COLOR_TEXT_HEADING,
  UI_COLOR_TEXT_HINT,
  UI_COLOR_WHITE
} from '../../../../constants/uiColors'

const RefundScanSearchControls = ({
  isEditMode,
  showCameraControls,
  search,
  manualError,
  hasSearch,
  hasSelectedLine = false,
  autoFocus = false,
  inputRef = null,
  onOpenCamera,
  onSearchChange,
  onSearchLine
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const isInputOpen = isEditMode || isFocused || Boolean(search)

  return (
    <>
      {showCameraControls && (
        <Box
          sx={{
            border: `1px dashed ${UI_COLOR_BORDER_NEUTRAL}`,
            borderRadius: 3,
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
            mb: 1.5,
            minHeight: { xs: 78, sm: 78 },
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 1.25, sm: 2 },
            backgroundColor: UI_COLOR_SURFACE_GHOST
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 800 }}>
              {REFUND_APPROVAL_TEXT.cameraScanTitle}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 10.5, sm: 11 },
                color: UI_COLOR_TEXT_HINT,
                mt: 0.5,
                maxWidth: { xs: 180, sm: 220 },
                lineHeight: 1.45
              }}
            >
              {REFUND_APPROVAL_TEXT.cameraScanSubtitle}
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={onOpenCamera}
            startIcon={<GoogleIcon name="photo_camera" size={20} color={UI_COLOR_WHITE} />}
            sx={{
              minWidth: { xs: 132, sm: 150 },
              width: { xs: 132, sm: 150 },
              height: 40,
              borderRadius: 1,
              backgroundColor: UI_COLOR_PRIMARY,
              px: { xs: 1, sm: 2 },
              fontSize: { xs: 12, sm: 13 },
              textTransform: 'none',
              whiteSpace: 'nowrap',
              '& .MuiButton-startIcon': {
                ml: 0,
                mr: { xs: 0.75, sm: 1 }
              },
              '&:hover': { backgroundColor: UI_COLOR_PRIMARY_ALT }
            }}
          >
            {REFUND_APPROVAL_TEXT.openCamera}
          </Button>
        </Box>
      )}

      <Box
        component="form"
        onSubmit={(event) => {
          event.preventDefault()
          if (isEditMode || !hasSearch) return
          onSearchLine()
        }}
      >
        <TextField
          fullWidth
          size="small"
          autoFocus={autoFocus}
          inputRef={inputRef}
          label={REFUND_APPROVAL_TEXT.searchCodeLabel}
          placeholder={isInputOpen ? REFUND_APPROVAL_TEXT.searchCodePlaceholder : undefined}
          value={search}
          error={Boolean(manualError)}
          helperText=" "
          onChange={onSearchChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            if (isEditMode || !hasSearch) return
            onSearchLine()
          }}
          InputLabelProps={{ shrink: isInputOpen || undefined }}
          InputProps={{
            readOnly: isEditMode,
            startAdornment: isInputOpen ? (
              <InputAdornment position="start">
                <GoogleIcon name="barcode_scanner" size={16} color={UI_COLOR_ICON_GRAY} />
              </InputAdornment>
            ) : undefined
          }}
          sx={{
            mt: isEditMode ? 1.5 : 2,
            mb: 0,
            '& .MuiInputBase-input': { fontSize: 14 },
            '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
              fontSize: 14,
              color: UI_COLOR_TEXT_HINT,
              transform: 'translate(14px, 9px) scale(1)'
            },
            '& .MuiInputLabel-shrink': {
              fontSize: isEditMode ? 16 : 12
            },
            ...(isEditMode && {
              '& .MuiInputLabel-root': {
                color: UI_COLOR_PRIMARY
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: UI_COLOR_WHITE,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: UI_COLOR_PRIMARY
                }
              },
              '& .MuiInputBase-input': {
                color: UI_COLOR_TEXT_HEADING
              }
            })
          }}
        />

        {!isEditMode && (
          <Button
            type="submit"
            variant="outlined"
            startIcon={<GoogleIcon name="search" size={18} />}
            onMouseDown={(event) => event.preventDefault()}
            disabled={!hasSearch}
            sx={{
              display: hasSelectedLine ? { xs: 'none', sm: 'inline-flex' } : 'inline-flex',
              mt: -1.5,
              mb: 3,
              width: 128,
              height: 32,
              borderRadius: 1,
              borderColor: UI_COLOR_PRIMARY,
              color: UI_COLOR_PRIMARY,
              fontSize: 11,
              fontWeight: 800,
              textTransform: 'none',
              '& .MuiButton-startIcon': {
                ml: 0,
                mr: 1
              },
              '&.Mui-disabled': {
                borderColor: UI_COLOR_BORDER_MEDIUM,
                color: UI_COLOR_TEXT_DISABLED
              },
              '&:hover': {
                borderColor: UI_COLOR_PRIMARY_ALT,
                backgroundColor: UI_COLOR_PRIMARY_SOFT_FAINT,
                color: UI_COLOR_PRIMARY_ALT
              }
            }}
          >
            {REFUND_APPROVAL_TEXT.searchLine}
          </Button>
        )}
      </Box>
    </>
  )
}

export default RefundScanSearchControls
